import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AdvancedFilter, FilterRule, FieldConfig } from '@/components/ui/advanced-filter'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { RefreshCw } from 'lucide-react'
import { MailboxEditDialog } from './MailboxEditDialog'
import { MailboxViewSelector } from './MailboxViewSelector'
import { MailboxColumnManagerDialog } from './MailboxColumnManagerDialog'
import { MailboxUploadDialog } from './MailboxUploadDialog'
import { MailboxHistoryDialog } from './MailboxHistoryDialog'
import { useMailboxViewSettings } from '@/hooks/useMailboxViewSettings'
import { useMailboxProperties } from '@/hooks/useMailboxProperties'
import { loadColumnConfigFromStorage } from '@/lib/mailbox-property-mapper'
import { TableColumnContextMenu } from '@/components/ui/table-column-context-menu'

import { Search, Settings, Edit, ChevronLeft, ChevronRight, Download, Upload, History } from 'lucide-react'
import { Mailbox } from '@/hooks/useMailboxes'
import { useToast } from '@/hooks/use-toast'
import { useClientsList } from '@/hooks/useClients'
import { getCalculatedFieldValue, isCalculatedField, setClientsCache } from '@/lib/mailbox-calculated-fields'
import { formatSignatureForDisplay } from '@/lib/signature-formatter'
import Papa from 'papaparse'

interface CompactMailboxTableProps {
  mailboxes: Mailbox[]
  isLoading: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  filters: FilterRule[]
  onFiltersChange: (filters: FilterRule[]) => void
  selectedMailboxes: string[]
  onSelectionChange: (selected: string[]) => void
  onMailboxUpdate: () => void
}

const MAILBOX_FILTER_FIELDS: FieldConfig[] = [
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'from_name', label: 'From Name', type: 'text' },
  { key: 'domain', label: 'Domain', type: 'text' },
  { key: 'type', label: 'Mailbox Type', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'DEACTIVATED', label: 'Deactivated' },
    { value: 'ORDERED/PURCHASED', label: 'ORDERED/PURCHASED' },
    { value: 'WAITING FOR UPLOAD', label: 'WAITING FOR UPLOAD' }
  ]},
  { key: 'sub_status', label: 'Sub Status', type: 'select', options: [
    { value: 'Warming up', label: 'Warming up' },
    { value: 'Ready to use', label: 'Ready to use' },
    { value: 'Recovering', label: 'Recovering' },
    { value: 'Burned', label: 'Burned' },
    { value: 'Deactivated', label: 'Deactivated' }
  ]},
  { key: 'batch_number', label: 'Batch', type: 'select', options: [
    { value: '1', label: 'Batch A' },
    { value: '2', label: 'Batch B' },
    { value: '3', label: 'Batch C' },
  ]},
  { key: 'warmup_status', label: 'Warmup Status', type: 'select', options: [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'PAUSED', label: 'Paused' }
  ]},
  { key: 'warmup_reputation', label: 'Warmup Reputation', type: 'number' },
  { key: 'current_daily_sent', label: 'Daily Sent', type: 'number' },
  { key: 'daily_limit', label: 'Daily Limit', type: 'number' },
  { key: 'provider', label: 'Provider', type: 'text' },
  { key: 'api_sync_status', label: 'API Sync Status', type: 'select', options: [
    { value: 'SUCCESS', label: 'Success' },
    { value: 'FAILURE', label: 'Failure' },
    { value: 'PENDING', label: 'Pending' }
  ]},
  { key: 'smartlead_connected', label: 'Smartlead Connected', type: 'boolean' },
  { key: 'masterinbox_connected', label: 'MasterInbox Connected', type: 'boolean' },
  { key: 'client_id', label: 'Client ID', type: 'number' },
  { key: 'mailbox_provider', label: 'Mailbox Provider', type: 'text' },
  { key: 'email_provider', label: 'Email Provider', type: 'text' },
  { key: 'total_emails_sent', label: 'Emails Sent', type: 'number' },
  { key: 'total_email_replies', label: 'Replies', type: 'number' },
  { key: 'total_human_replies', label: 'Human Replies', type: 'number' },
  { key: 'total_positive_replies', label: 'Positive Replies', type: 'number' },
  { key: 'total_email_bounces', label: 'Bounces', type: 'number' },
  { key: 'smtp_success', label: 'SMTP Status', type: 'boolean' },
  { key: 'imap_success', label: 'IMAP Status', type: 'boolean' },
  { key: 'notes', label: 'Notes', type: 'text' },
]

export function CompactMailboxTable({ 
  mailboxes, 
  isLoading, 
  searchQuery, 
  onSearchChange, 
  filters = [],
  onFiltersChange,
  selectedMailboxes,
  onSelectionChange,
  onMailboxUpdate
}: CompactMailboxTableProps) {
  const { data: clients } = useClientsList()
  const { properties } = useMailboxProperties()
  const { updateView, currentView, currentViewId } = useMailboxViewSettings()
  const { toast } = useToast()

  // Helper function to map database status values to UI display values
  const mapStatusFromDatabase = (dbStatus: string, apiSyncStatus: string): string => {
    // Check if mailbox is deactivated first
    if (apiSyncStatus === 'DEACTIVATED') {
      return 'Deactivated'
    }
    
    switch (dbStatus) {
      case 'ACTIVE':
        return 'Active'
      case 'INACTIVE':
        return 'Inactive'
      case 'DEACTIVATED':
        return 'Deactivated'
      case 'ORDERED/PURCHASED':
        return 'ORDERED/PURCHASED'
      case 'WAITING FOR UPLOAD':
        return 'WAITING FOR UPLOAD'
      default:
        return 'Active' // Default fallback
    }
  }

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [editingMailbox, setEditingMailbox] = useState<Mailbox | null>(null)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [showColumnManager, setShowColumnManager] = useState(false)
  const [showHistoryDialog, setShowHistoryDialog] = useState(false)
  const [historyMailbox, setHistoryMailbox] = useState<Mailbox | null>(null)
  const [searchTerm, setSearchTerm] = useState(searchQuery)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([])
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([])
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null)
  
  // Load column configuration on mount
  useEffect(() => {
    const { columns: loadedColumns } = loadColumnConfigFromStorage(properties, 'mailbox')
    const sortedVisibleColumns = loadedColumns
      .sort((a, b) => a.order - b.order)
      .filter(col => col.visible)
      .map(col => col.id)
    
    console.log('Loading initial column configuration:', sortedVisibleColumns)
    console.log('emails_sent_7_days included:', sortedVisibleColumns.includes('emails_sent_7_days'))
    console.log('signature included:', sortedVisibleColumns.includes('signature'))
    console.log('All available properties:', properties.map(p => p.id))
    setVisibleColumns(sortedVisibleColumns)
  }, [properties])
  
  // Initialize column labels with fallback values to prevent empty headers
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({
    email: 'Email',
    domain: 'Domain', 
    from_name: 'From Name',
    smartlead_connected: 'Smartlead',
    masterinbox_connected: 'MasterInbox',
    type: 'Type',
    signature: 'Signature',
    smtp_host: 'SMTP Host',
    smtp_port: 'SMTP Port',
    imap_host: 'IMAP Host',
    imap_port: 'IMAP Port',
    warmup_status: 'Warmup Status',
    warmup_reputation: 'Reputation',
    warmup: 'Warmup',
    provider: 'Provider',
    mailbox_provider: 'Mailbox Provider',
    email_provider: 'Email Provider',
    current_daily_sent: 'Sent Today',
    daily_limit: 'Daily Limit',
    smtp_success: 'SMTP Status',
    imap_success: 'IMAP Status',
    last_api_synced_at: 'Last Synced',
    total_emails_sent: 'Emails Sent',
    total_email_replies: 'Replies',
    total_human_replies: 'Human Replies',
    total_positive_replies: 'Positive Replies',
    total_email_bounces: 'Bounces',
    name_email: 'Name / Email',
    tags: 'Tags',
    notes: 'Notes'
  })

  // Update column labels from properties when available
  useEffect(() => {
    if (properties.length > 0) {
      const updatedLabels: Record<string, string> = { ...columnLabels }
      properties.forEach(prop => {
        updatedLabels[prop.id] = prop.label
      })
      setColumnLabels(updatedLabels)
    }
  }, [properties])
  
  // Update clients cache when clients data changes
  useEffect(() => {
    if (clients) {
      setClientsCache(clients)
    }
  }, [clients])

  // Load column visibility settings using unified storage system
  useEffect(() => {
    const loadColumnConfig = () => {
      try {
        const { columns, labels } = loadColumnConfigFromStorage(properties, 'mailbox')
        const visibleColumnIds = columns
          .filter(col => col.visible)
          .sort((a, b) => a.order - b.order)
          .map(col => col.id)
        
        console.log('Loading saved column config:', visibleColumnIds)
        setVisibleColumns(visibleColumnIds)
        setColumnLabels(labels)
      } catch (error) {
        console.error('Failed to load column config:', error)
      }
    }
    
    if (properties.length > 0) {
      loadColumnConfig()
    }
  }, [properties])

  // Sort mailboxes with Active status first by default
  const sortedMailboxes = React.useMemo(() => {
    if (!sortConfig) {
      // Default sorting: Active mailboxes first, then by email
      return [...mailboxes].sort((a, b) => {
        // First, sort by status (ACTIVE first)
        if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1
        if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1
        
        // Then sort by email alphabetically
        return a.email.localeCompare(b.email)
      })
    }
    
    // Apply custom sorting if sortConfig is set
    return [...mailboxes].sort((a, b) => {
      const aValue = a[sortConfig.column as keyof Mailbox]
      const bValue = b[sortConfig.column as keyof Mailbox]
      
      if (aValue === bValue) return 0
      
      const comparison = aValue < bValue ? -1 : 1
      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
  }, [mailboxes, sortConfig])

  // Calculate pagination
  const totalItems = sortedMailboxes.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedMailboxes = sortedMailboxes.slice(startIndex, endIndex)

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [mailboxes.length, searchQuery, filters])

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  const handleExport = () => {
    // Export all visible columns with their data
    const dataToExport = sortedMailboxes.map(mailbox => {
      const row: Record<string, any> = {}
      
      // Only include visible columns in the export
      visibleColumns.forEach(columnId => {
        const label = columnLabels[columnId] || columnId
        
        // Handle special fields that need transformation
        if (columnId === 'name_email') {
          row['From Name'] = mailbox.from_name || ''
          row['Email'] = mailbox.email || ''
          return
        } else if (columnId === 'tags') {
          row[label] = mailbox.tags?.map(t => t.name).join(', ') || ''
          return
        } else if (columnId === 'warmup') {
          const warmupStatus = mailbox.api_sync_status === 'DEACTIVATED' ? 'INACTIVE' : (mailbox.warmup_status || '')
          const rep = mailbox.api_sync_status === 'DEACTIVATED' ? 'N/A' : (String(mailbox.warmup_reputation || '0').replace('%', '') + '%')
          row[label] = `${warmupStatus} (${rep})`
          return
        } else if (columnId === 'status') {
          // Map status using the same logic as the display
          row[label] = mapStatusFromDatabase(mailbox.status || 'ACTIVE', mailbox.api_sync_status)
        } else if (columnId === 'warmup_status') {
          // Handle deactivated mailboxes for warmup status
          if (mailbox.api_sync_status === 'DEACTIVATED') {
            row[label] = 'INACTIVE'
          } else {
            row[label] = mailbox.warmup_status || ''
          }
        } else if (columnId === 'warmup_reputation') {
          // Handle deactivated mailboxes for reputation
          if (mailbox.api_sync_status === 'DEACTIVATED') {
            row[label] = 'N/A'
          } else {
            row[label] = mailbox.warmup_reputation || 'N/A'
          }
        } else if (columnId === 'signature') {
          // Format signature for display (preserve line breaks but remove HTML)
          row[label] = formatSignatureForDisplay(mailbox.signature)
        } else if (columnId === 'total_email_replies') {
          const replies = mailbox.total_email_replies || 0
          const sent = mailbox.total_emails_sent || 0
          const pct = sent > 0 ? ((replies / sent) * 100).toFixed(1) : '0.0'
          row[label] = `${replies} (${pct}%)`
        } else if (columnId === 'total_human_replies') {
          const human = (mailbox as any).total_human_replies || 0
          const replies = mailbox.total_email_replies || 0
          const pct = replies > 0 ? ((human / replies) * 100).toFixed(1) : '0.0'
          row[label] = `${human} (${pct}%)`
        } else if (columnId === 'total_positive_replies') {
          const positive = (mailbox as any).total_positive_replies || 0
          const human = (mailbox as any).total_human_replies || 0
          const pct = human > 0 ? ((positive / human) * 100).toFixed(1) : '0.0'
          row[label] = `${positive} (${pct}%)`
        } else if (columnId === 'total_email_bounces') {
          const bounces = mailbox.total_email_bounces || 0
          const sent = mailbox.total_emails_sent || 0
          const pct = sent > 0 ? ((bounces / sent) * 100).toFixed(1) : '0.0'
          row[label] = `${bounces} (${pct}%)`
        } else if (isCalculatedField(columnId)) {
          // Handle calculated fields
          row[label] = getCalculatedFieldValue(mailbox, columnId)
        } else {
          // Regular fields - get value directly from mailbox object
          row[label] = mailbox[columnId as keyof Mailbox] ?? ''
        }
      })
      
      return row
    })

    const csv = Papa.unparse(dataToExport)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `mailboxes-export-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast({
      title: "Export successful",
      description: `${sortedMailboxes.length} mailboxes exported with ${visibleColumns.length} columns`
    })
  }

  const getWarmupStatusBadge = (status: string, mailbox: any) => {
    // If mailbox is deactivated, warmup cannot be active
    if (mailbox.api_sync_status === 'DEACTIVATED') {
      return <Badge variant="destructive">INACTIVE</Badge>
    }
    
    const normalizedStatus = status?.toLowerCase()
    if (normalizedStatus === 'active' || normalizedStatus === 'enabled') {
      return <Badge className="bg-green-500 text-white">ACTIVE</Badge>
    }
    return <Badge variant="secondary">INACTIVE</Badge>
  }

  const getReputationIndicator = (reputation: number) => {
    const repValue = isNaN(reputation) ? 0 : reputation
    let colorClass = 'bg-red-500'
    let label = 'Poor'
    
    if (repValue >= 80) {
      colorClass = 'bg-green-500'
      label = 'Excellent'
    } else if (repValue >= 60) {
      colorClass = 'bg-yellow-500'
      label = 'Good'
    }
    
    return (
      <span 
        className={`inline-block w-2 h-2 rounded-full ${colorClass}`}
        aria-label={`Reputation: ${label} (${repValue}%)`}
        title={`${label} reputation: ${repValue}%`}
      />
    )
  }

  const getProgressIndicator = (current: number, max: number) => {
    const currentValue = current || 0
    const maxValue = max || 0
    const percentage = maxValue > 0 ? (currentValue / maxValue) * 100 : 0
    
    return (
      <div className="flex items-center gap-2" role="group" aria-label={`Email usage: ${currentValue} of ${maxValue}`}>
        <span className="text-sm font-medium" aria-label={`Current: ${currentValue}`}>
          {currentValue}
        </span>
        <div className="flex-1 bg-muted rounded-full h-1.5 max-w-[60px]" role="progressbar" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100}>
          <div 
            className="bg-primary h-1.5 rounded-full transition-all duration-300" 
            style={{ width: `${Math.min(percentage, 100)}%` }}
            title={`${currentValue}/${maxValue} (${Math.round(percentage)}%)`}
          />
        </div>
      </div>
    )
  }

  const getCalculatedFieldValue = (mailbox: Mailbox, fieldId: string): string | number => {
    switch (fieldId) {
      case 'days_warming_up':
        if (!mailbox.warmup_enabled_date) return 0
        const warmupDate = new Date(mailbox.warmup_enabled_date)
        const now = new Date()
        const diffTime = Math.abs(now.getTime() - warmupDate.getTime())
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        return diffDays

      case 'utilization_percentage':
        const current = mailbox.current_daily_sent || 0
        const limit = mailbox.daily_limit || 0
        return limit > 0 ? Math.round((current / limit) * 100) : 0

      case 'spam_rate':
        const spamTotal = mailbox.warmup_spam_total || 0
        const sentTotal = mailbox.warmup_sent_total || 0
        return sentTotal > 0 ? Math.round((spamTotal / sentTotal) * 100) : 0

      case 'emails_sent_7_days':
        // Calculate emails sent over last 7 days
        // This is a mock calculation - in a real implementation, you'd query the database
        // for actual email send data over the last 7 days
        const currentDailySent = mailbox.current_daily_sent || 0
        const dailyLimit = mailbox.daily_limit || 0
        
        // Mock calculation: assume 70% of daily limit was used on average over 7 days
        // In reality, you'd query actual send data from your email tracking system
        const averageDailyUsage = Math.round(dailyLimit * 0.7)
        const estimated7DayTotal = averageDailyUsage * 7
        
        console.log('emails_sent_7_days calculation:', {
          currentDailySent,
          dailyLimit,
          averageDailyUsage,
          estimated7DayTotal,
          mailbox: mailbox.email
        })
        
        return estimated7DayTotal

      case 'smartlead_connected':
        return (mailbox as any).smartlead_connected === true ? 'Yes' : (mailbox as any).smartlead_connected === false ? 'No' : (mailbox.api_sync_status === 'SUCCESS' ? 'Yes' : 'No')

      case 'masterinbox_connected':
        return (mailbox as any).masterinbox_connected === true ? 'Yes' : (mailbox as any).masterinbox_connected === false ? 'No' : '-'

      default:
        return '-'
    }
  }

  const isCalculatedField = (fieldId: string): boolean => {
    return ['days_warming_up', 'utilization_percentage', 'spam_rate', 'emails_sent_7_days', 'smartlead_connected', 'masterinbox_connected'].includes(fieldId)
  }

  // Get tooltip description for column headers
  const getColumnTooltip = (columnId: string): string => {
    const property = properties.find(p => p.id === columnId)
    if (!property) return ''
    
    // Use sourceDescription if available, otherwise create a basic description
    if (property.sourceDescription) {
      return property.sourceDescription
    }
    
    // Fallback descriptions for common columns
    const fallbackDescriptions: Record<string, string> = {
      'email': 'Primary email address for this mailbox',
      'domain': 'Email domain (e.g., gmail.com, outlook.com)',
      'from_name': 'Display name that appears in sent emails',
      'status': 'Current status of the mailbox (Active/Inactive/Deactivated)',
      'provider': 'Email service provider (Gmail, Outlook, etc.)',
      'type': 'Type of mailbox (Primary, Secondary, etc.)',
      'daily_limit': 'Maximum number of emails that can be sent per day',
      'current_daily_sent': 'Number of emails sent today',
      'message_per_day': 'Current daily usage vs daily limit',
      'warmup_status': 'Warmup status from Smartlead (Active/Inactive)',
      'warmup_reputation': 'Reputation score from warmup process',
      'smartlead_id': 'Unique identifier in Smartlead platform',
      'smartlead_connected': 'Whether mailbox is successfully connected to Smartlead (green = connected, red = not connected)',
      'masterinbox_connected': 'Whether mailbox is connected to MasterInbox (green = connected, red = not connected)',
      'last_synced': 'Last time data was synchronized with Smartlead',
      'days_warming_up': 'Number of days this mailbox has been in warmup',
      'utilization_percentage': 'Percentage of daily limit currently used',
      'spam_rate': 'Spam rate percentage for this mailbox',
      'emails_sent_7_days': 'Estimated total emails sent over the last 7 days',
      'bounce_rate': 'Email bounce rate from Smartlead analytics',
      'connection_health': 'Connection health status from Smartlead',
      'notes': 'Additional notes and comments about this mailbox'
    }
    
    return fallbackDescriptions[columnId] || `${property.label} information`
  }


  // Column management functions
  const handlePinColumn = (columnId: string) => {
    const newPinned = [...pinnedColumns, columnId]
    setPinnedColumns(newPinned)
    localStorage.setItem('mailbox-pinned-columns', JSON.stringify(newPinned))
  }

  const handleUnpinColumn = (columnId: string) => {
    const newPinned = pinnedColumns.filter(id => id !== columnId)
    setPinnedColumns(newPinned)
    localStorage.setItem('mailbox-pinned-columns', JSON.stringify(newPinned))
  }

  const handleHideColumn = (columnId: string) => {
    const newVisible = visibleColumns.filter(id => id !== columnId)
    setVisibleColumns(newVisible)
    
    // Update current view to persist column changes
    updateView(currentViewId, { visibleColumns: newVisible })
    
    // Update localStorage to maintain consistency
    const savedConfig = localStorage.getItem('mailbox-column-config')
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig)
      const updatedConfig = parsedConfig.map((col: any) => 
        col.id === columnId ? { ...col, visible: false } : col
      )
      localStorage.setItem('mailbox-column-config', JSON.stringify(updatedConfig))
    }
  }

  const handleShowColumn = (columnId: string) => {
    if (!visibleColumns.includes(columnId)) {
      const newVisible = [...visibleColumns, columnId]
      setVisibleColumns(newVisible)
      
      // Update current view to persist column changes
      updateView(currentViewId, { visibleColumns: newVisible })
      
      // Update localStorage to maintain consistency
      const savedConfig = localStorage.getItem('mailbox-column-config')
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig)
        const updatedConfig = parsedConfig.map((col: any) => 
          col.id === columnId ? { ...col, visible: true } : col
        )
        localStorage.setItem('mailbox-column-config', JSON.stringify(updatedConfig))
      }
    }
  }

  const handleRenameColumn = (columnId: string, newLabel: string) => {
    setColumnLabels(prev => ({
      ...prev,
      [columnId]: newLabel
    }))
    
    // Save to localStorage
    const newLabels = { ...columnLabels, [columnId]: newLabel }
    localStorage.setItem('mailbox-column-labels', JSON.stringify(newLabels))
  }

  const handleFilterColumn = (columnId: string) => {
    // Implement column-specific filtering if needed
    toast({
      title: "Filter applied",
      description: `Filtering by ${columnLabels[columnId]}`
    })
  }

  const handleSort = (columnId: string, direction: 'asc' | 'desc') => {
    setSortConfig({ column: columnId, direction })
    setCurrentPage(1) // Reset to first page when sorting
  }

  const handleEditMailbox = (mailbox: Mailbox) => {
    setEditingMailbox(mailbox)
    setShowEditDialog(true)
  }

  const handleViewHistory = (mailbox: Mailbox) => {
    setHistoryMailbox(mailbox)
    setShowHistoryDialog(true)
  }

  const handleMailboxUpdated = () => {
    setShowEditDialog(false)
    setEditingMailbox(null)
    onMailboxUpdate()
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading mailboxes...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <TooltipProvider>
      <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              Mailboxes
              {selectedMailboxes.length > 0 && (
                <span className="ml-2 text-sm font-normal text-blue-600">
                  ({selectedMailboxes.length} selected)
                </span>
              )}
            </CardTitle>
            <MailboxViewSelector 
              onViewChange={(view) => {
                // Apply view settings when changed
                setSearchTerm(view.searchQuery)
                setVisibleColumns(view.visibleColumns)
                if (onSearchChange) onSearchChange(view.searchQuery)
                if (onFiltersChange) onFiltersChange(view.filters)
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, client, or domain..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Advanced Filters
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                onMailboxUpdate()
                toast({
                  title: "Data refreshed",
                  description: "Mailbox data has been refreshed from the database"
                })
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={handleExport}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            
            <Button 
              size="sm"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>

            <Button 
              variant="outline"
              size="sm"
              onClick={() => setShowColumnManager(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Adjust Columns
            </Button>
          </div>
        </div>
        
        {showAdvancedFilters && (
          <AdvancedFilter
            fields={MAILBOX_FILTER_FIELDS}
            filters={filters || []}
            onFiltersChange={(newFilters) => {
              console.log('Advanced filters changed:', newFilters)
              if (onFiltersChange) {
                onFiltersChange(newFilters)
              }
            }}
            onApply={() => {
              console.log('Applying filters:', filters)
            }}
            onClear={() => {
              console.log('Clearing filters')
              if (onFiltersChange) {
                onFiltersChange([])
              }
            }}
          />
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <Table role="table" aria-label="Mailbox data table">
              <TableHeader>
                <TableRow className="bg-muted/50 border-b">
                  <TableHead className="w-12 font-medium text-foreground" scope="col">
                    <Checkbox
                      checked={selectedMailboxes.length === paginatedMailboxes.length && paginatedMailboxes.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onSelectionChange?.(paginatedMailboxes.map(m => m.id))
                        } else {
                          onSelectionChange?.([])
                        }
                      }}
                      aria-label="Select all mailboxes"
                    />
                  </TableHead>
                {visibleColumns.includes('name_email') && (
                  <TableColumnContextMenu
                    columnId="name_email"
                    columnLabel={columnLabels.name_email}
                    isPinned={pinnedColumns.includes('name_email')}
                    isVisible={visibleColumns.includes('name_email')}
                    currentSort={sortConfig}
                    onPin={handlePinColumn}
                    onUnpin={handleUnpinColumn}
                    onHide={handleHideColumn}
                    onShow={handleShowColumn}
                    onRename={handleRenameColumn}
                    onFilter={handleFilterColumn}
                    onSort={handleSort}
                  >
                    <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                      {columnLabels.name_email || 'Name / Email'}
                    </TableHead>
                  </TableColumnContextMenu>
                )}
                {visibleColumns.includes('email') && (
                  <TableColumnContextMenu
                    columnId="email"
                    columnLabel={columnLabels.email}
                    isPinned={pinnedColumns.includes('email')}
                    isVisible={visibleColumns.includes('email')}
                    currentSort={sortConfig}
                    onPin={handlePinColumn}
                    onUnpin={handleUnpinColumn}
                    onHide={handleHideColumn}
                    onShow={handleShowColumn}
                    onRename={handleRenameColumn}
                    onFilter={handleFilterColumn}
                    onSort={handleSort}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.email || 'Email'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('domain') && (
                   <TableColumnContextMenu
                     columnId="domain"
                     columnLabel={columnLabels.domain}
                     isPinned={pinnedColumns.includes('domain')}
                     isVisible={visibleColumns.includes('domain')}
                     currentSort={sortConfig}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                     onSort={handleSort}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.domain || 'Domain'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('from_name') && (
                   <TableColumnContextMenu
                     columnId="from_name"
                     columnLabel={columnLabels.from_name}
                     isPinned={pinnedColumns.includes('from_name')}
                     isVisible={visibleColumns.includes('from_name')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.from_name || 'From Name'}
                      </TableHead>
                   </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableColumnContextMenu
                      columnId="status"
                      columnLabel={columnLabels.status}
                      isPinned={pinnedColumns.includes('status')}
                      isVisible={visibleColumns.includes('status')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.status || 'Status'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('smartlead_connected') && (
                    <TableColumnContextMenu
                      columnId="smartlead_connected"
                      columnLabel={columnLabels.smartlead_connected}
                      isPinned={pinnedColumns.includes('smartlead_connected')}
                      isVisible={visibleColumns.includes('smartlead_connected')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.smartlead_connected || 'Smartlead'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('masterinbox_connected') && (
                    <TableColumnContextMenu
                      columnId="masterinbox_connected"
                      columnLabel={columnLabels.masterinbox_connected}
                      isPinned={pinnedColumns.includes('masterinbox_connected')}
                      isVisible={visibleColumns.includes('masterinbox_connected')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.masterinbox_connected || 'MasterInbox'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                   {visibleColumns.includes('type') && (
                     <TableColumnContextMenu
                       columnId="type"
                       columnLabel={columnLabels.type}
                       isPinned={pinnedColumns.includes('type')}
                       isVisible={visibleColumns.includes('type')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                         <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.type || 'Type'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                 {visibleColumns.includes('smtp_host') && (
                   <TableColumnContextMenu
                     columnId="smtp_host"
                     columnLabel={columnLabels.smtp_host}
                     isPinned={pinnedColumns.includes('smtp_host')}
                     isVisible={visibleColumns.includes('smtp_host')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.smtp_host || 'SMTP Host'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('smtp_port') && (
                   <TableColumnContextMenu
                     columnId="smtp_port"
                     columnLabel={columnLabels.smtp_port}
                     isPinned={pinnedColumns.includes('smtp_port')}
                     isVisible={visibleColumns.includes('smtp_port')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.smtp_port || 'SMTP Port'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('imap_host') && (
                   <TableColumnContextMenu
                     columnId="imap_host"
                     columnLabel={columnLabels.imap_host}
                     isPinned={pinnedColumns.includes('imap_host')}
                     isVisible={visibleColumns.includes('imap_host')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.imap_host || 'IMAP Host'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('imap_port') && (
                   <TableColumnContextMenu
                     columnId="imap_port"
                     columnLabel={columnLabels.imap_port}
                     isPinned={pinnedColumns.includes('imap_port')}
                     isVisible={visibleColumns.includes('imap_port')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.imap_port || 'IMAP Port'}
                      </TableHead>
                   </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('warmup') && (
                    <TableColumnContextMenu
                      columnId="warmup"
                      columnLabel={columnLabels.warmup}
                      isPinned={pinnedColumns.includes('warmup')}
                      isVisible={visibleColumns.includes('warmup')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.warmup || 'Warmup'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('warmup_status') && (
                    <TableColumnContextMenu
                      columnId="warmup_status"
                      columnLabel={columnLabels.warmup_status}
                      isPinned={pinnedColumns.includes('warmup_status')}
                      isVisible={visibleColumns.includes('warmup_status')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.warmup_status || 'Warmup Status'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('warmup_reputation') && (
                    <TableColumnContextMenu
                      columnId="warmup_reputation"
                      columnLabel={columnLabels.warmup_reputation}
                      isPinned={pinnedColumns.includes('warmup_reputation')}
                      isVisible={visibleColumns.includes('warmup_reputation')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.warmup_reputation || 'Reputation'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                 {visibleColumns.includes('provider') && (
                   <TableColumnContextMenu
                     columnId="provider"
                     columnLabel={columnLabels.provider}
                     isPinned={pinnedColumns.includes('provider')}
                     isVisible={visibleColumns.includes('provider')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.provider || 'Provider'}
                      </TableHead>
                   </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('mailbox_provider') && (
                    <TableColumnContextMenu
                      columnId="mailbox_provider"
                      columnLabel={columnLabels.mailbox_provider}
                      isPinned={pinnedColumns.includes('mailbox_provider')}
                      isVisible={visibleColumns.includes('mailbox_provider')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.mailbox_provider || 'Mailbox Provider'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('email_provider') && (
                    <TableColumnContextMenu
                      columnId="email_provider"
                      columnLabel={columnLabels.email_provider}
                      isPinned={pinnedColumns.includes('email_provider')}
                      isVisible={visibleColumns.includes('email_provider')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.email_provider || 'Email Provider'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('message_per_day') && (
                    <TableColumnContextMenu
                      columnId="message_per_day"
                      columnLabel={columnLabels.message_per_day}
                      isPinned={pinnedColumns.includes('message_per_day')}
                      isVisible={visibleColumns.includes('message_per_day')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.message_per_day || 'Daily Usage'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('client_name') && (
                    <TableColumnContextMenu
                      columnId="client_name"
                      columnLabel={columnLabels.client_name}
                      isPinned={pinnedColumns.includes('client_name')}
                      isVisible={visibleColumns.includes('client_name')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.client_name || 'Client'}
                       </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('current_daily_sent') && (
                   <TableColumnContextMenu
                     columnId="current_daily_sent"
                     columnLabel={columnLabels.current_daily_sent}
                     isPinned={pinnedColumns.includes('current_daily_sent')}
                     isVisible={visibleColumns.includes('current_daily_sent')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.current_daily_sent || 'Sent Today'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('daily_limit') && (
                   <TableColumnContextMenu
                     columnId="daily_limit"
                     columnLabel={columnLabels.daily_limit}
                     isPinned={pinnedColumns.includes('daily_limit')}
                     isVisible={visibleColumns.includes('daily_limit')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.daily_limit || 'Daily Limit'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('smtp_success') && (
                   <TableColumnContextMenu
                     columnId="smtp_success"
                     columnLabel={columnLabels.smtp_success}
                     isPinned={pinnedColumns.includes('smtp_success')}
                     isVisible={visibleColumns.includes('smtp_success')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.smtp_success || 'SMTP Status'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                 {visibleColumns.includes('imap_success') && (
                   <TableColumnContextMenu
                     columnId="imap_success"
                     columnLabel={columnLabels.imap_success}
                     isPinned={pinnedColumns.includes('imap_success')}
                     isVisible={visibleColumns.includes('imap_success')}
                     onPin={handlePinColumn}
                     onUnpin={handleUnpinColumn}
                     onHide={handleHideColumn}
                     onShow={handleShowColumn}
                     onRename={handleRenameColumn}
                     onFilter={handleFilterColumn}
                   >
                       <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                        {columnLabels.imap_success || 'IMAP Status'}
                      </TableHead>
                   </TableColumnContextMenu>
                 )}
                   {visibleColumns.includes('last_api_synced_at') && (
                    <TableColumnContextMenu
                      columnId="last_api_synced_at"
                      columnLabel={columnLabels.last_api_synced_at}
                      isPinned={pinnedColumns.includes('last_api_synced_at')}
                      isVisible={visibleColumns.includes('last_api_synced_at')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.last_api_synced_at || 'Last Synced'}
                       </TableHead>
                    </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('days_warming_up') && (
                     <TableColumnContextMenu
                       columnId="days_warming_up"
                       columnLabel={columnLabels.days_warming_up}
                       isPinned={pinnedColumns.includes('days_warming_up')}
                       isVisible={visibleColumns.includes('days_warming_up')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.days_warming_up || 'Days Warming Up'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('api_sync_status') && (
                     <TableColumnContextMenu
                       columnId="api_sync_status"
                       columnLabel={columnLabels.api_sync_status}
                       isPinned={pinnedColumns.includes('api_sync_status')}
                       isVisible={visibleColumns.includes('api_sync_status')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                         <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.api_sync_status || 'API Sync Status'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('client_id') && (
                     <TableColumnContextMenu
                       columnId="client_id"
                       columnLabel={columnLabels.client_id}
                       isPinned={pinnedColumns.includes('client_id')}
                       isVisible={visibleColumns.includes('client_id')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                         <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.client_id || 'Client ID'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('smartlead_id') && (
                     <TableColumnContextMenu
                       columnId="smartlead_id"
                       columnLabel={columnLabels.smartlead_id}
                       isPinned={pinnedColumns.includes('smartlead_id')}
                       isVisible={visibleColumns.includes('smartlead_id')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                         <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.smartlead_id || 'Smartlead ID'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('deactivated_at') && (
                     <TableColumnContextMenu
                       columnId="deactivated_at"
                       columnLabel={columnLabels.deactivated_at}
                       isPinned={pinnedColumns.includes('deactivated_at')}
                       isVisible={visibleColumns.includes('deactivated_at')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.deactivated_at || 'Deactivated At'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('mailbox_cost') && (
                     <TableColumnContextMenu
                       columnId="mailbox_cost"
                       columnLabel={columnLabels.mailbox_cost}
                       isPinned={pinnedColumns.includes('mailbox_cost')}
                       isVisible={visibleColumns.includes('mailbox_cost')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.mailbox_cost || 'Mailbox Cost'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('purchase_date') && (
                     <TableColumnContextMenu
                       columnId="purchase_date"
                       columnLabel={columnLabels.purchase_date}
                       isPinned={pinnedColumns.includes('purchase_date')}
                       isVisible={visibleColumns.includes('purchase_date')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.purchase_date || 'Purchase Date'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('utilization_percentage') && (
                     <TableColumnContextMenu
                       columnId="utilization_percentage"
                       columnLabel={columnLabels.utilization_percentage}
                       isPinned={pinnedColumns.includes('utilization_percentage')}
                       isVisible={visibleColumns.includes('utilization_percentage')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.utilization_percentage || 'Utilization %'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('spam_rate') && (
                     <TableColumnContextMenu
                       columnId="spam_rate"
                       columnLabel={columnLabels.spam_rate}
                       isPinned={pinnedColumns.includes('spam_rate')}
                       isVisible={visibleColumns.includes('spam_rate')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.spam_rate || 'Spam Rate'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('emails_sent_7_days') && (
                     <TableColumnContextMenu
                       columnId="emails_sent_7_days"
                       columnLabel={columnLabels.emails_sent_7_days}
                       isPinned={pinnedColumns.includes('emails_sent_7_days')}
                       isVisible={visibleColumns.includes('emails_sent_7_days')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.emails_sent_7_days || 'Emails Sent (7 Days)'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('total_emails_sent') && (
                     <TableColumnContextMenu
                       columnId="total_emails_sent"
                       columnLabel={columnLabels.total_emails_sent}
                       isPinned={pinnedColumns.includes('total_emails_sent')}
                       isVisible={visibleColumns.includes('total_emails_sent')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.total_emails_sent || 'Emails Sent'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('total_email_replies') && (
                     <TableColumnContextMenu
                       columnId="total_email_replies"
                       columnLabel={columnLabels.total_email_replies}
                       isPinned={pinnedColumns.includes('total_email_replies')}
                       isVisible={visibleColumns.includes('total_email_replies')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.total_email_replies || 'Replies'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('total_email_bounces') && (
                     <TableColumnContextMenu
                       columnId="total_email_bounces"
                       columnLabel={columnLabels.total_email_bounces}
                       isPinned={pinnedColumns.includes('total_email_bounces')}
                       isVisible={visibleColumns.includes('total_email_bounces')}
                       onPin={handlePinColumn}
                       onUnpin={handleUnpinColumn}
                       onHide={handleHideColumn}
                       onShow={handleShowColumn}
                       onRename={handleRenameColumn}
                       onFilter={handleFilterColumn}
                     >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                          {columnLabels.total_email_bounces || 'Bounces'}
                        </TableHead>
                     </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('tags') && (
                    <TableColumnContextMenu
                      columnId="tags"
                      columnLabel={columnLabels.tags}
                      isPinned={pinnedColumns.includes('tags')}
                      isVisible={visibleColumns.includes('tags')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.tags || 'Tags'}
                       </TableHead>
                    </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('signature') && (
                    <TableColumnContextMenu
                      columnId="signature"
                      columnLabel={columnLabels.signature}
                      isPinned={pinnedColumns.includes('signature')}
                      isVisible={visibleColumns.includes('signature')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.signature || 'Signature'}
                       </TableHead>
                    </TableColumnContextMenu>
                   )}
                   {visibleColumns.includes('notes') && (
                    <TableColumnContextMenu
                      columnId="notes"
                      columnLabel={columnLabels.notes}
                      isPinned={pinnedColumns.includes('notes')}
                      isVisible={visibleColumns.includes('notes')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                        <TableHead className="font-semibold text-xs cursor-pointer" scope="col">
                         {columnLabels.notes || 'Notes'}
                       </TableHead>
                    </TableColumnContextMenu>
                   )}
                  <TableHead className="font-semibold text-xs" scope="col">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedMailboxes.map((mailbox) => (
                <TableRow 
                  key={mailbox.id}
                  className="hover:bg-muted/50"
                >
                  <TableCell className="w-12">
                    <Checkbox
                      checked={selectedMailboxes.includes(mailbox.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onSelectionChange?.([...selectedMailboxes, mailbox.id])
                        } else {
                          onSelectionChange?.(selectedMailboxes.filter(id => id !== mailbox.id))
                        }
                      }}
                      aria-label={`Select ${mailbox.email}`}
                    />
                  </TableCell>
                  {visibleColumns.includes('name_email') && (
                    <TableCell className="py-3">
                      <div className="font-medium text-sm">{mailbox.from_name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{mailbox.email}</div>
                    </TableCell>
                  )}
                  {visibleColumns.includes('email') && (
                    <TableCell className="py-3 font-medium">
                      {mailbox.email}
                    </TableCell>
                  )}
                  {visibleColumns.includes('domain') && (
                    <TableCell className="py-3 font-medium">
                      {mailbox.domain || '-'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('from_name') && (
                    <TableCell className="py-3">
                      {mailbox.from_name || '-'}
                    </TableCell>
                   )}
                   {visibleColumns.includes('status') && (
                     <TableCell className="py-3">
                       <Badge variant={mailbox.api_sync_status === 'DEACTIVATED' ? 'destructive' : 'default'}>
                         {mapStatusFromDatabase(mailbox.status || 'ACTIVE', mailbox.api_sync_status)}
                       </Badge>
                     </TableCell>
                   )}
                   {visibleColumns.includes('smartlead_connected') && (
                     <TableCell className="py-3 text-center">
                       {isCalculatedField('smartlead_connected') ? (
                         <div className="flex items-center justify-center">
                           {getCalculatedFieldValue(mailbox, 'smartlead_connected') === 'Yes' ? (
                             <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                           ) : (
                             <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                           )}
                         </div>
                       ) : (
                         <div className="flex items-center justify-center">
                           {mailbox.api_sync_status === 'SUCCESS' ? (
                             <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                           ) : (
                             <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                           )}
                         </div>
                       )}
                     </TableCell>
                   )}
                   {visibleColumns.includes('masterinbox_connected') && (
                     <TableCell className="py-3 text-center">
                       <div className="flex items-center justify-center">
                         {getCalculatedFieldValue(mailbox, 'masterinbox_connected') === 'Yes' ? (
                           <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                         ) : getCalculatedFieldValue(mailbox, 'masterinbox_connected') === 'No' ? (
                           <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                         ) : (
                           <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                         )}
                       </div>
                     </TableCell>
                   )}
                   {visibleColumns.includes('type') && (
                     <TableCell className="py-3">
                       <Badge variant="outline">
                         {mailbox.type || 'Unknown'}
                       </Badge>
                     </TableCell>
                   )}
                  {visibleColumns.includes('smtp_host') && (
                    <TableCell className="py-3 font-mono text-sm">
                      {mailbox.smtp_host || '-'}
                    </TableCell>
                  )}
                   {visibleColumns.includes('smtp_port') && (
                     <TableCell className="py-3">
                       {mailbox.smtp_port || '-'}
                     </TableCell>
                   )}
                   {visibleColumns.includes('imap_host') && (
                     <TableCell className="py-3 font-mono text-sm">
                       {mailbox.imap_host || '-'}
                     </TableCell>
                   )}
                   {visibleColumns.includes('imap_port') && (
                     <TableCell className="py-3">
                       {mailbox.imap_port || '-'}
                     </TableCell>
                   )}
                   {visibleColumns.includes('warmup') && (
                     <TableCell className="py-3">
                       <div>{getWarmupStatusBadge(mailbox.warmup_status, mailbox)}</div>
                       <div className="flex items-center gap-1 mt-1">
                         {mailbox.api_sync_status === 'DEACTIVATED' ? (
                           <span className="text-xs text-muted-foreground">N/A</span>
                         ) : mailbox.warmup_reputation === 'N/A' ? (
                           <span className="text-xs text-muted-foreground">N/A</span>
                         ) : (
                           <>
                             {getReputationIndicator(parseInt(String(mailbox.warmup_reputation || '0').replace('%', '')))}
                             <span className="text-xs">{String(mailbox.warmup_reputation || '0').replace('%', '')}%</span>
                           </>
                         )}
                       </div>
                     </TableCell>
                   )}
                   {visibleColumns.includes('warmup_status') && (
                     <TableCell className="py-3">
                       {getWarmupStatusBadge(mailbox.warmup_status, mailbox)}
                     </TableCell>
                   )}
                      {visibleColumns.includes('warmup_reputation') && (
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2">
                            {mailbox.api_sync_status === 'DEACTIVATED' ? (
                              <span className="text-muted-foreground">N/A</span>
                            ) : (
                              <>
                                {mailbox.warmup_reputation === 'N/A' ? (
                                  <span className="text-muted-foreground">N/A</span>
                                ) : (
                                  <>
                                    {getReputationIndicator(parseInt(String(mailbox.warmup_reputation || '0').replace('%', '')))}
                                    <span>{String(mailbox.warmup_reputation || '0').replace('%', '')}%</span>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                     )}
                   {visibleColumns.includes('provider') && (
                     <TableCell className="py-3">
                       {mailbox.provider || '-'}
                      </TableCell>
                    )}
                    {visibleColumns.includes('mailbox_provider') && (
                      <TableCell className="py-3">
                        <span className="text-sm">{mailbox.mailbox_provider || '-'}</span>
                      </TableCell>
                    )}
                    {visibleColumns.includes('email_provider') && (
                      <TableCell className="py-3">
                        <span className="text-sm">{mailbox.email_provider || '-'}</span>
                      </TableCell>
                    )}
                    {visibleColumns.includes('message_per_day') && (
                      <TableCell className="py-3">
                        {mailbox.current_daily_sent !== null && mailbox.daily_limit !== null 
                          ? `${mailbox.current_daily_sent} / ${mailbox.daily_limit}`
                          : mailbox.message_per_day || '-'
                        }
                      </TableCell>
                    )}
                     {visibleColumns.includes('client_name') && (
                       <TableCell className="py-3">
                         {getCalculatedFieldValue(mailbox, 'client_name')}
                       </TableCell>
                     )}
                     {visibleColumns.includes('current_daily_sent') && (
                      <TableCell className="py-3">
                        {getProgressIndicator(mailbox.current_daily_sent || 0, mailbox.daily_limit || 0)}
                      </TableCell>
                    )}
                   {visibleColumns.includes('daily_limit') && (
                     <TableCell className="py-3">
                       {mailbox.daily_limit || 0}
                     </TableCell>
                   )}
                  {visibleColumns.includes('smtp_success') && (
                    <TableCell className="py-3">
                      <Badge variant={mailbox.smtp_success ? "default" : "destructive"}>
                        {mailbox.smtp_success ? 'Connected' : 'Failed'}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.includes('imap_success') && (
                    <TableCell className="py-3">
                      <Badge variant={mailbox.imap_success ? "default" : "destructive"}>
                        {mailbox.imap_success ? 'Connected' : 'Failed'}
                      </Badge>
                    </TableCell>
                  )}
                    {visibleColumns.includes('last_api_synced_at') && (
                      <TableCell className="py-3 text-sm text-muted-foreground">
                        {mailbox.last_api_synced_at ? 
                          (() => {
                            const syncedDate = new Date(mailbox.last_api_synced_at)
                            const now = new Date()
                            // Check if date is in the future (likely bad data)
                            if (syncedDate > now) {
                              return 'Invalid Date'
                            }
                            return syncedDate.toLocaleDateString()
                          })() : 
                          'Never'
                        }
                      </TableCell>
                    )}
                    {visibleColumns.includes('days_warming_up') && (
                      <TableCell className="py-3 text-sm font-medium">
                        {getCalculatedFieldValue(mailbox, 'days_warming_up')} days
                      </TableCell>
                    )}
                    {visibleColumns.includes('utilization_percentage') && (
                      <TableCell className="py-3 text-sm">
                        {getCalculatedFieldValue(mailbox, 'utilization_percentage')}%
                      </TableCell>
                    )}
                    {visibleColumns.includes('spam_rate') && (
                      <TableCell className="py-3 text-sm">
                        {getCalculatedFieldValue(mailbox, 'spam_rate')}%
                      </TableCell>
                     )}
                     {visibleColumns.includes('api_sync_status') && (
                       <TableCell className="py-3">
                         <Badge variant={mailbox.api_sync_status === 'SUCCESS' ? 'default' : 'destructive'}>
                           {mailbox.api_sync_status || 'Unknown'}
                         </Badge>
                       </TableCell>
                     )}
                     {visibleColumns.includes('client_id') && (
                       <TableCell className="py-3">
                         {mailbox.client_id || '-'}
                       </TableCell>
                     )}
                     {visibleColumns.includes('emails_sent_7_days') && (
                       <TableCell className="py-3 text-sm font-medium">
                         {(() => {
                           const calculatedValue = getCalculatedFieldValue(mailbox, 'emails_sent_7_days')
                           console.log('emails_sent_7_days rendering:', {
                             mailbox: mailbox.email,
                             smartlead_id: mailbox.smartlead_id,
                             calculatedValue,
                             current_daily_sent: mailbox.current_daily_sent,
                             daily_limit: mailbox.daily_limit
                           })
                           return calculatedValue
                         })()}
                       </TableCell>
                     )}
                     {visibleColumns.includes('total_emails_sent') && (
                       <TableCell className="py-3 text-sm font-medium">
                         {(mailbox.total_emails_sent || 0).toLocaleString()}
                       </TableCell>
                     )}
                     {visibleColumns.includes('total_email_replies') && (
                       <TableCell className="py-3 text-sm">
                         <span className="font-medium">
                           {(mailbox.total_email_replies || 0).toLocaleString()}
                           <span className="text-muted-foreground font-normal ml-1">
                             ({(mailbox.total_emails_sent || 0) > 0
                               ? `${(((mailbox.total_email_replies || 0) / mailbox.total_emails_sent!) * 100).toFixed(1)}%`
                               : '0.0%'})
                           </span>
                         </span>
                       </TableCell>
                     )}
                     {visibleColumns.includes('total_email_bounces') && (
                       <TableCell className="py-3 text-sm">
                         <span className="font-medium">
                           {(mailbox.total_email_bounces || 0).toLocaleString()}
                           <span className="text-muted-foreground font-normal ml-1">
                             ({(mailbox.total_emails_sent || 0) > 0
                               ? `${(((mailbox.total_email_bounces || 0) / mailbox.total_emails_sent!) * 100).toFixed(1)}%`
                               : '0.0%'})
                           </span>
                         </span>
                       </TableCell>
                     )}
                     {visibleColumns.includes('smartlead_id') && (
                       <TableCell className="py-3">
                         {mailbox.smartlead_id || '-'}
                       </TableCell>
                     )}
                     {visibleColumns.includes('deactivated_at') && (
                       <TableCell className="py-3 text-sm text-muted-foreground">
                         {mailbox.deactivated_at ? 
                           new Date(mailbox.deactivated_at).toLocaleDateString() : 
                           '-'
                         }
                       </TableCell>
                     )}
                     {visibleColumns.includes('mailbox_cost') && (
                       <TableCell className="py-3">
                         {mailbox.mailbox_cost || '-'}
                       </TableCell>
                     )}
                     {visibleColumns.includes('purchase_date') && (
                       <TableCell className="py-3 text-sm text-muted-foreground">
                         {mailbox.purchase_date ? 
                           new Date(mailbox.purchase_date).toLocaleDateString() : 
                           '-'
                         }
                       </TableCell>
                     )}
                     {visibleColumns.includes('tags') && (
                      <TableCell className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {mailbox.tags && mailbox.tags.length > 0
                            ? mailbox.tags.map(tag => (
                                <Badge key={tag.id} variant="outline" className="text-[10px] px-1.5 py-0">
                                  {tag.name}
                                </Badge>
                              ))
                            : <span className="text-muted-foreground">-</span>
                          }
                        </div>
                      </TableCell>
                     )}
                     {visibleColumns.includes('signature') && (
                      <TableCell className="py-3 max-w-[200px] truncate">
                        {mailbox.signature ? (
                          <span title={formatSignatureForDisplay(mailbox.signature)}>
                            {(() => {
                              const displaySignature = formatSignatureForDisplay(mailbox.signature)
                              return displaySignature.length > 30 ?
                                `${displaySignature.substring(0, 30)}...` :
                                displaySignature
                            })()}
                          </span>
                        ) : '-'}
                      </TableCell>
                     )}
                     {visibleColumns.includes('notes') && (
                      <TableCell className="py-3 text-sm text-muted-foreground max-w-[200px]">
                        {mailbox.notes ? (
                          <span 
                            className="cursor-pointer hover:text-foreground transition-colors truncate block" 
                            title={mailbox.notes}
                          >
                            {mailbox.notes.length > 30 ? 
                              `${mailbox.notes.substring(0, 30)}...` : 
                              mailbox.notes
                            }
                          </span>
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell className="py-3">
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewHistory(mailbox)}
                          title="View mailbox history"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditMailbox(mailbox)}
                          title="Edit mailbox"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedMailboxes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} className="text-center text-muted-foreground">
                    No mailboxes found
                  </TableCell>
                </TableRow>
              )}
             </TableBody>
           </Table>
          </div>
        </div>
        
        {/* Pagination at bottom right */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} mailboxes
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      
      <MailboxEditDialog
        mailbox={editingMailbox}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onUpdate={handleMailboxUpdated}
      />

      <MailboxUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUploadComplete={() => {
          setShowUploadDialog(false)
          onMailboxUpdate()
        }}
      />

      <MailboxColumnManagerDialog
        open={showColumnManager}
        onOpenChange={setShowColumnManager}
        onColumnsUpdated={(columns) => {
          // Sort columns by their order property and filter visible ones
          const sortedVisibleColumns = columns
            .sort((a, b) => a.order - b.order)
            .filter(col => col.visible)
            .map(col => col.id)
          
          console.log('Updating columns with proper order:', sortedVisibleColumns)
          setVisibleColumns(sortedVisibleColumns)
          updateView(currentViewId, { visibleColumns: sortedVisibleColumns })
        }}
      />

      <MailboxHistoryDialog
        mailbox={historyMailbox}
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
      />
    </Card>
    </TooltipProvider>
  )
}